import {
  CheckCircle2,
  Clock3,
  Image as ImageIcon,
  ExternalLink,
  MessageSquare,
  Settings2,
  ShieldCheck,
  Wrench,
  type LucideIcon,
} from 'lucide-react'
import { useState, type CSSProperties, type ReactNode } from 'react'

import type {
  DecisionState,
  SharedAddress,
  SharedCustomer,
  SharedPublication,
  SharedQuote,
  SharedQuoteLine,
} from '@/api/quote'
import { EquipmentComparison } from '@/components/quote/equipment-comparison'
import { lineLabel, lineTitle, protectionPlanText } from '@/lib/quote'
import { cn, formatMoney } from '@/lib/utils'

const dateFormat = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
})
const sumMoney = (values: Array<string | null>) =>
  (values.reduce((sum, value) => sum + Math.round(Number(value || 0) * 100), 0) / 100).toFixed(2)

export function LineImage({ line, icon: Icon }: { line: SharedQuoteLine; icon: LucideIcon }) {
  const [failedUrl, setFailedUrl] = useState<string | null>(null)
  const url = line.primary_asset?.url
  return url && url !== failedUrl ? (
    <img
      alt=""
      className="aspect-square w-full rounded-md object-contain"
      loading="lazy"
      onError={() => setFailedUrl(url)}
      src={url}
    />
  ) : (
    <span className="bg-muted grid aspect-square w-full place-items-center rounded-md">
      <Icon aria-hidden="true" className="text-muted-foreground size-4" />
    </span>
  )
}

function Fulfillment({ line }: { line: SharedQuoteLine }) {
  const label = line.fulfillment?.lead_time?.label
  if (!label) return null
  return (
    <p className="text-muted-foreground mt-1 flex items-center gap-1.5 text-xs">
      <Clock3 aria-hidden="true" className="size-3.5" />
      {label}
    </p>
  )
}

/** The quote exactly as published: pictured items, totals and terms (demo `QuoteDocument`). */
export function QuoteDocument({
  quote,
  customer,
  publication,
  expiresAt,
  onComment,
  headerActions,
}: {
  quote: SharedQuote
  customer: SharedCustomer
  publication: SharedPublication
  expiresAt: string
  onComment?: (line: SharedQuoteLine) => void
  headerActions?: ReactNode
}) {
  const showPricing = publication.visible_sections.includes('pricing')
  const installations = quote.lines.filter(
    (line) => line.installation_for && line.line_type === 'service',
  )
  const installationGroup = new Set(installations.map((line) => line.row_ref))
  const serviceRows = new Set<string>()
  const rowDepth = new Map<string, number>()
  for (const line of quote.lines) {
    rowDepth.set(
      line.row_ref,
      line.parent_row_ref ? (rowDepth.get(line.parent_row_ref) ?? 0) + 1 : 0,
    )
    if (
      line.line_type === 'service' ||
      line.line_type === 'custom' ||
      (line.parent_row_ref && serviceRows.has(line.parent_row_ref))
    ) {
      serviceRows.add(line.row_ref)
    }
    if (line.parent_row_ref && installationGroup.has(line.parent_row_ref))
      installationGroup.add(line.row_ref)
  }
  // Installation details share one displayed price; comments on it reference the whole group.
  const installation: SharedQuoteLine[] = installations.length
    ? [
        {
          row_ref: 'display:installation',
          position: 0,
          parent_row_ref: null,
          display_number: '',
          line_type: 'service',
          description: 'Installation',
          quantity: '1',
          installation_for: null,
          sell_amount: null,
          extended_sell: sumMoney(
            quote.lines
              .filter((line) => installationGroup.has(line.row_ref))
              .map((line) => line.extended_sell),
          ),
          notes: null,
          manufacturer: null,
          model: null,
          specifications: [],
          spec_text: null,
          spec_text_edited: false,
          alternative_comparison: null,
          fulfillment: null,
          protection_plan: null,
          primary_asset: null,
        },
      ]
    : []
  const groups = [
    {
      title: 'Equipment',
      lines: quote.lines.filter((line) => !serviceRows.has(line.row_ref)),
    },
    {
      title: 'Services',
      lines: [
        ...quote.lines.filter(
          (line) => serviceRows.has(line.row_ref) && !installationGroup.has(line.row_ref),
        ),
        ...installation,
      ],
    },
  ].filter((group) => group.lines.length)

  return (
    <article className="bg-card text-card-foreground">
      <header className="border-b px-5 pt-7 pb-6 sm:px-10 sm:pt-9">
        <div className="grid min-w-0 gap-5 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
          <div className="min-w-0">
            <p className="text-muted-foreground mb-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm [overflow-wrap:anywhere]">
              Quote {quote.quote_number}
              {publication.mode === 'draft' ? (
                <span className="border-usa-navy/20 text-usa-navy rounded-[4px] border px-2 py-0.5 text-xs font-semibold">
                  Draft for review
                </span>
              ) : null}
            </p>
            <h1 className="text-usa-navy text-3xl leading-tight font-extrabold tracking-tight [overflow-wrap:anywhere] sm:text-4xl">
              {quote.title}
            </h1>
            <span aria-hidden="true" className="bg-usa-red mt-4 block h-[3px] w-16" />
          </div>
          {headerActions}
        </div>
      </header>
      <div className="px-5 py-7 sm:px-10 sm:py-9">
        <div className="grid gap-5 border-b pb-6 sm:grid-cols-[minmax(0,1fr)_auto]">
          <div>
            <p className="text-sm font-semibold">Prepared for {customer.company_name}</p>
            {customer.contact_name ? (
              <p className="text-muted-foreground mt-0.5 text-sm">{customer.contact_name}</p>
            ) : null}
          </div>
          <p className="text-muted-foreground flex items-start gap-1.5 text-sm sm:text-right">
            <Clock3 aria-hidden="true" className="mt-0.5 size-4" />
            Valid through {dateFormat.format(new Date(expiresAt))}
          </p>
          <div className="grid gap-4 text-sm sm:col-span-2 sm:grid-cols-2">
            <Address address={quote.bill_to} customer={customer} label="Bill to" />
            <Address address={quote.deliver_to} customer={customer} label="Ship to" />
          </div>
        </div>
        <div className="mt-7 grid gap-8">
          {groups.map((group) => (
            <section aria-labelledby={`group-${group.title}`} key={group.title}>
              <h2
                className="text-muted-foreground mb-2 text-xs font-semibold tracking-wide uppercase"
                id={`group-${group.title}`}
              >
                {group.title}
              </h2>
              <div className="divide-y border-y">
                {group.lines.map((line) => {
                  // AutoQuotes' specification text (or Sales's edit); a line with none shows its description.
                  const specifications = line.spec_text?.trim() || line.description
                  // The manufacturer and SKU on one line, the specifications under it.
                  const title = lineTitle(line)
                  const Icon = line.protection_plan
                    ? ShieldCheck
                    : line.line_type === 'configuration'
                      ? Settings2
                      : line.line_type !== 'product'
                        ? Wrench
                        : ImageIcon
                  const priced =
                    showPricing && (line.sell_amount !== null || line.extended_sell !== null)
                  return (
                    <div
                      className={cn(
                        'relative grid grid-cols-[44px_minmax(0,1fr)_auto] items-start gap-x-3 gap-y-1',
                        line.parent_row_ref
                          ? 'ml-[calc(var(--row-depth)*0.75rem)] border-l py-2.5 pl-3 sm:ml-[calc(var(--row-depth)*1.5rem)]'
                          : 'py-4',
                        priced
                          ? 'sm:grid-cols-[56px_minmax(0,1fr)_auto_120px]'
                          : 'sm:grid-cols-[56px_minmax(0,1fr)_auto]',
                      )}
                      key={line.row_ref}
                      style={
                        {
                          '--row-depth': rowDepth.get(line.row_ref) ?? 0,
                        } as CSSProperties
                      }
                    >
                      <div className="text-center">
                        {!line.parent_row_ref && line.display_number ? (
                          <span className="text-muted-foreground mb-1 block text-xs font-semibold tabular-nums">
                            {line.display_number}
                          </span>
                        ) : null}
                        <LineImage icon={Icon} line={line} />
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-sm font-medium break-words">{title}</h3>
                        {specifications && specifications !== title ? (
                          <p className="text-muted-foreground mt-0.5 text-sm break-words whitespace-pre-line">
                            {specifications}
                          </p>
                        ) : null}
                        {line.protection_plan ? (
                          <div className="text-muted-foreground mt-0.5 grid gap-0.5 text-xs">
                            {protectionPlanText(line.protection_plan).map((text) => (
                              <p key={text}>{text}</p>
                            ))}
                            {/* eslint-disable-next-line no-restricted-syntax -- Safeware's site */}
                            <a
                              className="text-usa-blue inline-flex min-h-6 w-fit items-center gap-1 font-medium hover:underline"
                              href={line.protection_plan.terms_url}
                              rel="noopener noreferrer"
                              target="_blank"
                            >
                              Plan terms
                              <ExternalLink aria-hidden="true" className="size-3" />
                            </a>
                          </div>
                        ) : null}
                        {line.installation_for ? (
                          <p className="text-muted-foreground mt-0.5 text-xs">
                            {line.installation_for}
                          </p>
                        ) : null}
                        {line.notes ? (
                          <p className="text-muted-foreground mt-1 text-xs">{line.notes}</p>
                        ) : null}
                        <Fulfillment line={line} />
                      </div>
                      <div className="grid justify-items-end gap-1 text-xs">
                        <span className="text-muted-foreground whitespace-nowrap">
                          Qty{' '}
                          <strong className="text-foreground ml-1 tabular-nums">
                            {Number(line.quantity)}
                          </strong>
                        </span>
                        {onComment ? (
                          <button
                            aria-label={`Comment on ${lineLabel(line)}`}
                            className="text-usa-blue hover:bg-muted focus-visible:outline-usa-blue grid size-9 place-items-center rounded-md focus-visible:outline-2"
                            onClick={() => onComment(line)}
                            title="Comment on this item"
                            type="button"
                          >
                            <MessageSquare aria-hidden="true" className="size-4" />
                          </button>
                        ) : null}
                      </div>
                      {priced ? (
                        <div className="col-span-2 col-start-2 text-sm sm:col-span-1 sm:col-start-auto sm:text-right">
                          {line.sell_amount !== null ? (
                            <span className="text-muted-foreground mr-3 text-xs sm:mr-0 sm:block">
                              {formatMoney(line.sell_amount)} each
                            </span>
                          ) : null}
                          {line.extended_sell !== null ? (
                            <strong className="tabular-nums">
                              {formatMoney(line.extended_sell)}
                            </strong>
                          ) : null}
                        </div>
                      ) : null}
                      {publication.mode === 'draft' &&
                      line.alternative_comparison?.alternatives.length ? (
                        <details className="col-span-full mt-2 min-w-0 border-t">
                          <summary className="text-usa-blue cursor-pointer py-2 text-sm font-medium">
                            Compare alternatives ({line.alternative_comparison.alternatives.length})
                          </summary>
                          <EquipmentComparison comparison={line.alternative_comparison} />
                        </details>
                      ) : null}
                    </div>
                  )
                })}
              </div>
            </section>
          ))}
        </div>
        {quote.notes ? (
          <section className="mt-8">
            <h2 className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
              Notes
            </h2>
            <p className="mt-2 text-sm whitespace-pre-line">{quote.notes}</p>
          </section>
        ) : null}
        {showPricing && quote.totals ? (
          <dl className="mt-8 ml-auto grid max-w-sm gap-2 text-sm">
            <Total label="Equipment" value={quote.totals.equipment_subtotal} />
            {Number(quote.totals.services_subtotal) ? (
              <Total label="Services" value={quote.totals.services_subtotal} />
            ) : null}
            {Number(quote.totals.other_subtotal) ? (
              <Total label="Other" value={quote.totals.other_subtotal} />
            ) : null}
            <Total label="Freight" value={quote.totals.freight} />
            <div className="border-t pt-3">
              <Total
                label="Subtotal"
                value={sumMoney([quote.totals.sell_subtotal, quote.totals.freight])}
              />
            </div>
            {Number(quote.totals.rebate) ? (
              <Total label="Rebate" value={`-${quote.totals.rebate}`} />
            ) : null}
            <Total label="Tax" value={quote.totals.tax} />
            <div className="mt-1 flex justify-between border-t pt-3 text-base">
              <dt className="font-semibold">Total</dt>
              <dd className="font-bold tabular-nums">{formatMoney(quote.totals.grand_total)}</dd>
            </div>
            {Number(quote.totals.deposit) ? (
              <>
                <Total label="Deposit" value={quote.totals.deposit} />
                <Total label="Balance" value={quote.totals.balance} />
              </>
            ) : null}
          </dl>
        ) : null}
        {quote.terms?.balance_terms ? (
          <section className="mt-8 border-t pt-5">
            <h2 className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
              Terms
            </h2>
            <p className="mt-2 text-sm">{quote.terms.balance_terms}</p>
          </section>
        ) : null}
        <footer className="text-muted-foreground mt-9 flex items-end justify-between gap-4 border-t pt-5 text-xs">
          <span>{showPricing ? 'Prices in USD.' : null}</span>
          <div className="text-right">
            <p className="text-foreground font-semibold">
              Revision {publication.publication_number}
            </p>
            <p className="mt-1">{dateFormat.format(new Date(publication.published_at))}</p>
          </div>
        </footer>
      </div>
    </article>
  )
}

function Address({
  label,
  address,
  customer,
}: {
  label: string
  address: SharedAddress | null
  customer: SharedCustomer
}) {
  if (!address?.line1) return null
  const locality = [address.city, [address.state, address.postal_code].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ')
  return (
    <div>
      <h2 className="text-muted-foreground mb-1 text-xs font-semibold">{label}</h2>
      <address className="leading-5 not-italic">
        {address.name && address.name !== customer.company_name ? (
          <span className="block">{address.name}</span>
        ) : null}
        {address.contact_name && address.contact_name !== customer.contact_name ? (
          <span className="block">{address.contact_name}</span>
        ) : null}
        <span className="block">{[address.line1, address.line2].filter(Boolean).join(', ')}</span>
        <span className="block">
          {locality}
          {address.country && address.country !== 'US' ? ` · ${address.country}` : ''}
        </span>
      </address>
    </div>
  )
}

function Total({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-medium tabular-nums">{formatMoney(value)}</dd>
    </div>
  )
}

export function DecisionBanner({ state }: { state: DecisionState }) {
  if (state === 'pending') return null
  const approved = state === 'approved'
  return (
    <div
      className={cn(
        'flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold',
        approved ? 'bg-emerald-700 text-white' : 'bg-amber-100 text-amber-950',
      )}
      role="status"
    >
      <CheckCircle2 aria-hidden="true" className="size-4" />
      {approved ? 'Quote approved' : state === 'rejected' ? 'Quote declined' : 'Changes requested'}
    </div>
  )
}
